﻿import { useState, useRef } from 'react';
import { Button } from 'devextreme-react/button';

interface Props {
    onFileSelected: (file: File | null) => void;
    multiple?: boolean;        // to support multiple files
    disabled?: boolean;
    maxSizeBytes?: number;     // optional: reject oversized files
    blacklistExtensions?: string[]; // e.g., ['.exe', '.dll', '.jar']
    // accept is only for UI hint to browser's file picker filter list; not for enforcement
    accept?: string[];           // e.g., [".pdf", ".docx", "image/*"]
    onError?: (message: string) => void;
}

export default function FileLoader(props: Props) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isDragging, setIsDragging] = useState(false);

    const acceptString = props.accept?.map(a => a.toLowerCase()).join(',');

    const handleBrowseClick = () => {
        if (!props.disabled) {
            fileInputRef.current?.click();
        }
    };

    const isExtensionBlacklisted = (file: File): boolean => {
        if (!props.blacklistExtensions || props.blacklistExtensions.length === 0) {
            return false;
        }

        const fileName = file.name.toLowerCase();
        const extension = fileName.includes('.')
            ? '.' + fileName.split('.').pop()
            : '';

        return props.blacklistExtensions
            .map(ext => ext.toLowerCase())
            .includes(extension);
    };

    const selectFirstValidFile = (files: FileList | null) => {
        if (!files || files.length === 0) {
            props.onFileSelected(null);
            return;
        }

        const file = files[0];

        // Block blacklisted extensions
        if (isExtensionBlacklisted(file)) {
            props.onError?.(
                `Files of type ${file.name.split('.').pop()?.toUpperCase()} are not allowed.`
            );
            props.onFileSelected(null);
            return;
        }

        // Enforce max size
        if (props.maxSizeBytes && file.size > props.maxSizeBytes) {
            props.onError?.(`File is too large. Max size is ${Math.round(props.maxSizeBytes / 1024 / 1024)} MB.`);
            props.onFileSelected(null);
            return;
        }

        props.onFileSelected(file);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            selectFirstValidFile(e.target.files);
        }
        // Reset so the same file triggers 'onChange' if picked again
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        // Some browsers put files in dataTransfer.items
        const dt = e.dataTransfer;
        if (dt?.files && dt.files.length > 0) {
            selectFirstValidFile(dt.files);
        } else {
            props.onError?.('No files dropped.');
            props.onFileSelected(null);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!props.disabled) setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    return (
        <div
            role="button"
            className={`file-loader-container ${isDragging ? 'dragging' : ''}`}
            tabIndex={0}
            onClick={handleBrowseClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBrowseClick(); }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            aria-disabled={props.disabled}
        >

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept={acceptString}
                multiple={props.multiple}
                disabled={props.disabled}
            />

            <i className="dx-icon-doc de-default-icon-color dx-default-icon-size-md"></i>
            <p>Drag and drop file here</p>
            <p>— or —</p>

            <Button
                text="Browse Files"
                type="default"
                stylingMode="outlined"
                onClick={(e) => {
                    e.event?.stopPropagation();
                    handleBrowseClick();
                }}
                disabled={props.disabled}
            />
        </div>
    );
}